import React, { useState, useEffect, useCallback } from 'react';
import { purchaseAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';
import { TrendingUp, DollarSign, ShoppingBag, Users, Truck, ArrowRightLeft, Calendar, Package, IndianRupee } from 'lucide-react';

const COLORS = ['#0e7490', '#0891b2', '#22d3ee', '#67e8f9', '#a5f3fc', '#155e75', '#164e63', '#083344', '#06b6d4', '#0284c7'];
const STATUS_COLORS = { active: '#22c55e', closed: '#6b7280', cancelled: '#ef4444', draft: '#eab308', received: '#3b82f6' };

function formatCurrency(v) {
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v?.toFixed(0) || '0';
}

export default function SpendAnalyticsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('year');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [view, setView] = useState('orders'); // 'orders' or 'received'
  const [itemType, setItemType] = useState('');

  const ITEM_TYPES = [
    'Raw Material', 'Service', 'Consumable', 'Semi-Finished Good',
    'Finished Good', 'Fixed Asset', 'Bought Out Part',
  ];

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period, view };
      if (itemType) params.item_type = itemType;
      if (period === 'custom' && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      const res = await purchaseAPI.getSpendAnalytics(params);
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Access denied: Only purchase managers can view analytics');
      } else {
        toast.error('Failed to load analytics');
      }
    } finally { setLoading(false); }
  }, [period, startDate, endDate, view, itemType]);

  useEffect(() => {
    if (period !== 'custom') loadAnalytics();
  }, [period, view, itemType]);

  const handleCustomApply = () => {
    if (!startDate || !endDate) { toast.error('Select both dates'); return; }
    loadAnalytics();
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">Loading analytics...</div>;
  }

  const s = data?.summary || {};
  const deliveryPerfData = data?.delivery_performance ? [
    { name: 'On Time', value: data.delivery_performance.on_time, fill: '#22c55e' },
    { name: 'Delayed', value: data.delivery_performance.delayed, fill: '#ef4444' },
    { name: 'Pending', value: data.delivery_performance.pending, fill: '#eab308' },
  ].filter(d => d.value > 0) : [];

  const poStatusData = (data?.po_status_breakdown || []).map(d => ({
    ...d, fill: STATUS_COLORS[d.status] || '#6b7280',
  }));

  return (
    <div className="space-y-5" data-testid="spend-analytics-page">
      {/* Header & Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-heading font-semibold tracking-tight">Spend Analytics</h1>
          <p className="text-xs text-muted-foreground">
            {data?.date_range?.from} to {data?.date_range?.to}
            {data?.item_type_filter && <span className="ml-1 text-cyan-700 font-medium">| {data.item_type_filter}</span>}
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {/* View Toggle */}
          <div className="flex rounded-md border overflow-hidden" data-testid="view-toggle">
            <button
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${view === 'orders' ? 'bg-cyan-700 text-white' : 'bg-white text-muted-foreground hover:bg-muted/50'}`}
              onClick={() => setView('orders')}
              data-testid="view-orders-btn"
            >
              <ShoppingBag className="w-3 h-3 inline mr-1" />Orders
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${view === 'received' ? 'bg-cyan-700 text-white' : 'bg-white text-muted-foreground hover:bg-muted/50'}`}
              onClick={() => setView('received')}
              data-testid="view-received-btn"
            >
              <Truck className="w-3 h-3 inline mr-1" />Received
            </button>
          </div>
          {/* Item Type Filter */}
          <Select value={itemType || '_all'} onValueChange={v => setItemType(v === '_all' ? '' : v)}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="item-type-filter">
              <SelectValue placeholder="All Item Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All Item Types</SelectItem>
              {ITEM_TYPES.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={period} onValueChange={v => setPeriod(v)}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid="period-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          {period === 'custom' && (
            <>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-8 text-xs w-32" data-testid="start-date" />
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-8 text-xs w-32" data-testid="end-date" />
              <Button size="sm" className="h-8 text-xs" onClick={handleCustomApply} data-testid="apply-custom">
                <Calendar className="w-3 h-3 mr-1" /> Apply
              </Button>
            </>
          )}
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card data-testid="kpi-total-spend">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 rounded bg-cyan-100"><DollarSign className="w-3.5 h-3.5 text-cyan-700" /></div>
              <span className="text-[10px] text-muted-foreground font-medium">
                {view === 'received' ? 'Total Received Value' : 'Total Spend'}
              </span>
            </div>
            <p className="text-xl font-bold tracking-tight">{formatCurrency(s.total_spend || 0)}</p>
            <p className="text-[10px] text-muted-foreground">
              {view === 'received' ? `${s.total_receipts || 0} receipts` : `${s.total_pos || 0} purchase requests`}
            </p>
          </CardContent>
        </Card>
        <Card data-testid="kpi-avg-po">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 rounded bg-violet-100"><ShoppingBag className="w-3.5 h-3.5 text-violet-700" /></div>
              <span className="text-[10px] text-muted-foreground font-medium">
                {view === 'received' ? 'Avg Receipt Value' : 'Avg PR Value'}
              </span>
            </div>
            <p className="text-xl font-bold tracking-tight">{formatCurrency(s.avg_po_value || s.avg_receipt_value || 0)}</p>
          </CardContent>
        </Card>
        <Card data-testid="kpi-suppliers">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 rounded bg-emerald-100"><Users className="w-3.5 h-3.5 text-emerald-700" /></div>
              <span className="text-[10px] text-muted-foreground font-medium">Suppliers</span>
            </div>
            <p className="text-xl font-bold tracking-tight">{s.unique_suppliers || 0}</p>
          </CardContent>
        </Card>
        <Card data-testid="kpi-delivery">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 rounded bg-amber-100">
                {view === 'received' ? <TrendingUp className="w-3.5 h-3.5 text-amber-700" /> : <Truck className="w-3.5 h-3.5 text-amber-700" />}
              </div>
              <span className="text-[10px] text-muted-foreground font-medium">
                {view === 'received' ? 'Items Received' : 'On-Time Delivery'}
              </span>
            </div>
            <p className="text-xl font-bold tracking-tight">
              {view === 'received' ? (s.total_items_received || 0) : `${data?.delivery_performance?.on_time_pct || 0}%`}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ===== Combined Spend Overview ===== */}
      {data?.combined_spend && (
        <Card className="border-l-4 border-l-cyan-600" data-testid="combined-spend-card">
          <CardContent className="p-4">
            <h3 className="text-xs font-semibold mb-3 flex items-center gap-1.5">
              <IndianRupee className="w-3.5 h-3.5 text-cyan-700" /> Combined Procurement Spend
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-[10px] text-muted-foreground font-medium">Total Combined Spend</p>
                <p className="text-xl font-bold tracking-tight text-cyan-800">{formatCurrency(data.combined_spend.total || 0)}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground font-medium">PO / PR Spend</p>
                <p className="text-lg font-bold tracking-tight">{formatCurrency(data.combined_spend.po_spend || 0)}</p>
                <p className="text-[10px] text-cyan-600">{data.combined_spend.po_pct || 0}% of total</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground font-medium">Misc / Non-PO Spend</p>
                <p className="text-lg font-bold tracking-tight text-orange-700">{formatCurrency(data.combined_spend.misc_spend || 0)}</p>
                <p className="text-[10px] text-orange-600">{data.combined_spend.misc_pct || 0}% of total</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground font-medium">Subcontract Spend</p>
                <p className="text-lg font-bold tracking-tight text-violet-700">{formatCurrency(data.combined_spend.subcontract_spend || 0)}</p>
                <p className="text-[10px] text-violet-600">{data.combined_spend.subcontract_pct || 0}% of total</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground font-medium mb-1.5">Spend Split</p>
                <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden flex">
                  <div className="h-full bg-cyan-600 transition-all" style={{ width: `${data.combined_spend.po_pct || 0}%` }} />
                  <div className="h-full bg-orange-500 transition-all" style={{ width: `${data.combined_spend.misc_pct || 0}%` }} />
                  <div className="h-full bg-violet-500 transition-all" style={{ width: `${data.combined_spend.subcontract_pct || 0}%` }} />
                </div>
                <div className="flex gap-3 mt-1 flex-wrap">
                  <span className="text-[9px] flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-cyan-600" /> PO</span>
                  <span className="text-[9px] flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-orange-500" /> Misc</span>
                  <span className="text-[9px] flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-violet-500" /> Subcontract</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Spend Chart */}
      <Card>
        <CardContent className="p-4">
          <h3 className="text-xs font-medium mb-3">Monthly Spend Trend</h3>
          {data?.monthly_spend?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.monthly_spend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={formatCurrency} />
                <Tooltip formatter={(v) => [`${formatCurrency(v)}`, '']} labelStyle={{ fontSize: 11 }} contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {data.top_suppliers?.slice(0, 5).map((sup, i) => (
                  <Bar key={sup.name} dataKey={sup.name} stackId="a" fill={COLORS[i % COLORS.length]} radius={i === (Math.min(5, data.top_suppliers.length) - 1) ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-10">No data for selected period</p>
          )}
        </CardContent>
      </Card>

      {/* Row: Top Suppliers + Category Breakdown */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Top Suppliers */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-xs font-medium mb-3">Top Suppliers by Spend</h3>
            {data?.top_suppliers?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.top_suppliers} layout="vertical" margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={formatCurrency} />
                  <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 9 }} />
                  <Tooltip formatter={(v) => [`${formatCurrency(v)}`, 'Spend']} contentStyle={{ fontSize: 11 }} />
                  <Bar dataKey="total" fill="#0e7490" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-10">No supplier data</p>
            )}
          </CardContent>
        </Card>

        {/* Category Breakdown */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-xs font-medium mb-3">Spend by Category</h3>
            {data?.category_breakdown?.length > 0 ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="55%" height={220}>
                  <PieChart>
                    <Pie data={data.category_breakdown} dataKey="value" nameKey="category" cx="50%" cy="50%" outerRadius={85} innerRadius={45}>
                      {data.category_breakdown.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-1.5">
                  {data.category_breakdown.slice(0, 6).map((cat, i) => (
                    <div key={i} className="flex items-center gap-2 text-[10px]">
                      <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <span className="truncate flex-1">{cat.category}</span>
                      <span className="font-medium">{formatCurrency(cat.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-10">No category data</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row: PO Status + Delivery Performance + Indent Conversion (Orders view only) */}
      {view === 'orders' && (
      <div className="grid md:grid-cols-3 gap-4">
        {/* PO Status Breakdown */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-xs font-medium mb-3">PR Status Breakdown</h3>
            {poStatusData.length > 0 ? (
              <div className="space-y-2">
                {poStatusData.map((d, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: d.fill }} />
                    <span className="text-xs flex-1 capitalize">{d.status}</span>
                    <Badge variant="outline" className="text-[10px]">{d.count}</Badge>
                  </div>
                ))}
                <div className="pt-2 border-t flex justify-between text-xs">
                  <span className="text-muted-foreground">Total</span>
                  <span className="font-bold">{poStatusData.reduce((a, b) => a + b.count, 0)}</span>
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-10">No PO data</p>
            )}
          </CardContent>
        </Card>

        {/* Delivery Performance */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-xs font-medium mb-3 flex items-center gap-1.5">
              <Truck className="w-3.5 h-3.5" /> Delivery Performance
            </h3>
            {deliveryPerfData.length > 0 ? (
              <div className="flex items-center gap-3">
                <ResponsiveContainer width="50%" height={160}>
                  <PieChart>
                    <Pie data={deliveryPerfData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} innerRadius={35}>
                      {deliveryPerfData.map((d, i) => (
                        <Cell key={i} fill={d.fill} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {deliveryPerfData.map((d, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.fill }} />
                      <span>{d.name}</span>
                      <span className="font-bold">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-10">No delivery data</p>
            )}
          </CardContent>
        </Card>

        {/* Indent-to-PO Conversion */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-xs font-medium mb-3 flex items-center gap-1.5">
              <ArrowRightLeft className="w-3.5 h-3.5" /> Indent Conversion
            </h3>
            {data?.indent_conversion ? (
              <div className="text-center space-y-3">
                <div className="relative w-24 h-24 mx-auto">
                  <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="#0e7490" strokeWidth="3"
                      strokeDasharray={`${data.indent_conversion.conversion_rate * 0.975} 100`}
                      strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-bold">{data.indent_conversion.conversion_rate}%</span>
                  </div>
                </div>
                <div className="flex justify-center gap-4 text-xs">
                  <div>
                    <p className="text-muted-foreground">Total</p>
                    <p className="font-bold">{data.indent_conversion.total_indents}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Converted</p>
                    <p className="font-bold text-cyan-700">{data.indent_conversion.converted}</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-10">No indent data</p>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* Top Items Received (Received view only) */}
      {view === 'received' && data?.top_items?.length > 0 && (
        <Card data-testid="top-items-received">
          <CardContent className="p-4">
            <h3 className="text-xs font-semibold mb-3">Top Items by Received Value</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-2 font-medium text-muted-foreground">#</th>
                    <th className="text-left p-2 font-medium text-muted-foreground">Item Code</th>
                    <th className="text-left p-2 font-medium text-muted-foreground">Item Name</th>
                    <th className="text-right p-2 font-medium text-muted-foreground">Qty Received</th>
                    <th className="text-right p-2 font-medium text-muted-foreground">Total Value</th>
                    <th className="text-left p-2 font-medium text-muted-foreground">Suppliers</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_items.map((item, idx) => (
                    <tr key={idx} className="border-b last:border-b-0 hover:bg-muted/30">
                      <td className="p-2 text-muted-foreground">{idx + 1}</td>
                      <td className="p-2 font-mono text-[10px]">{item.unique_id || '-'}</td>
                      <td className="p-2">{item.item_name}</td>
                      <td className="p-2 text-right font-medium">{item.total_qty}</td>
                      <td className="p-2 text-right font-medium">{formatCurrency(item.total_value)}</td>
                      <td className="p-2 text-[10px] text-muted-foreground">{(item.suppliers || []).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Supplier Table */}
      {data?.top_suppliers?.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-xs font-medium mb-3">Supplier {view === 'received' ? 'Received' : 'Spend'} Details</h3>
            <table className="w-full text-xs" data-testid="supplier-spend-table">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-2 font-medium text-muted-foreground">#</th>
                  <th className="text-left p-2 font-medium text-muted-foreground">Supplier</th>
                  <th className="text-right p-2 font-medium text-muted-foreground">{view === 'received' ? 'Receipts' : 'PRs'}</th>
                  <th className="text-right p-2 font-medium text-muted-foreground">{view === 'received' ? 'Total Received' : 'Total Spend'}</th>
                  <th className="text-right p-2 font-medium text-muted-foreground">% of Total</th>
                </tr>
              </thead>
              <tbody>
                {data.top_suppliers.map((sup, idx) => (
                  <tr key={idx} className="border-b hover:bg-muted/50">
                    <td className="p-2 text-muted-foreground">{idx + 1}</td>
                    <td className="p-2 font-medium">{sup.name}</td>
                    <td className="p-2 text-right">{sup.po_count}</td>
                    <td className="p-2 text-right font-mono font-medium">{formatCurrency(sup.total)}</td>
                    <td className="p-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-1.5">
                          <div className="h-1.5 rounded-full bg-cyan-600" style={{ width: `${Math.min(100, (sup.total / (s.total_spend || 1)) * 100)}%` }} />
                        </div>
                        <span>{((sup.total / (s.total_spend || 1)) * 100).toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* ===== Misc Spend Breakdown Section ===== */}
      {data?.misc_spend && data.misc_spend.total_spend > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="misc-spend-section">
          {/* Misc Spend - Top Suppliers */}
          <Card>
            <CardContent className="p-4">
              <h3 className="text-xs font-semibold mb-3 flex items-center gap-1.5">
                <Package className="w-3.5 h-3.5 text-orange-600" /> Misc Spend — Top Suppliers
              </h3>
              <div className="text-[10px] text-muted-foreground mb-2">
                {data.misc_spend.total_receipts} receipts | {data.misc_spend.unique_suppliers} suppliers
              </div>
              {data.misc_spend.top_suppliers?.length > 0 ? (
                <div className="space-y-2">
                  {data.misc_spend.top_suppliers.map((sup, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-2 py-1 border-b border-dashed last:border-0">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{sup.name}</p>
                        <p className="text-[10px] text-muted-foreground">{sup.count} receipt(s)</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs font-bold font-mono">{formatCurrency(sup.total)}</p>
                        <div className="w-16 bg-gray-100 rounded-full h-1.5 mt-0.5">
                          <div className="h-1.5 rounded-full bg-orange-500" style={{ width: `${Math.min(100, (sup.total / (data.misc_spend.total_spend || 1)) * 100)}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground text-center py-4">No data</p>
              )}
            </CardContent>
          </Card>

          {/* Misc Spend - By Program */}
          <Card>
            <CardContent className="p-4">
              <h3 className="text-xs font-semibold mb-3 flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-purple-600" /> Misc Spend — By Program
              </h3>
              {data.misc_spend.by_program?.length > 0 ? (
                <div className="space-y-2">
                  {data.misc_spend.by_program.map((prog, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-2 py-1 border-b border-dashed last:border-0">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{prog.program_name}</p>
                        <p className="text-[10px] text-muted-foreground">{prog.count} receipt(s)</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs font-bold font-mono">{formatCurrency(prog.total)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground text-center py-4">No misc receipts linked to programs yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== Subcontract Spend Breakdown Section ===== */}
      {data?.subcontract_spend && data.subcontract_spend.total_spend > 0 && (
        <Card className="border-l-4 border-l-violet-500" data-testid="subcontract-spend-section">
          <CardContent className="p-4">
            <h3 className="text-xs font-semibold mb-3 flex items-center gap-1.5">
              <Truck className="w-3.5 h-3.5 text-violet-600" /> Subcontract / Job Work Spend
            </h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-[10px] text-muted-foreground font-medium">Total Invoiced</p>
                <p className="text-lg font-bold tracking-tight text-violet-700">{formatCurrency(data.subcontract_spend.total_spend)}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground font-medium">Service Entries</p>
                <p className="text-lg font-bold tracking-tight">{data.subcontract_spend.total_entries}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground font-medium">Unique Suppliers</p>
                <p className="text-lg font-bold tracking-tight">{data.subcontract_spend.unique_suppliers}</p>
              </div>
            </div>
            {data.subcontract_spend.top_suppliers?.length > 0 && (
              <>
                <p className="text-[10px] text-muted-foreground font-medium mb-2">Top Suppliers by Invoiced Amount</p>
                <div className="space-y-2">
                  {data.subcontract_spend.top_suppliers.map((sup, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-2 py-1 border-b border-dashed last:border-0">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{sup.name}</p>
                        <p className="text-[10px] text-muted-foreground">{sup.count} service entry(s)</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs font-bold font-mono">{formatCurrency(sup.total)}</p>
                        <div className="w-16 bg-gray-100 rounded-full h-1.5 mt-0.5">
                          <div className="h-1.5 rounded-full bg-violet-500" style={{ width: `${Math.min(100, (sup.total / (data.subcontract_spend.total_spend || 1)) * 100)}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {data.subcontract_spend.monthly?.length > 0 && (
              <div className="mt-4">
                <p className="text-[10px] text-muted-foreground font-medium mb-2">Monthly Subcontract Spend</p>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={data.subcontract_spend.monthly} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={formatCurrency} />
                    <Tooltip formatter={(v) => [`INR ${v?.toLocaleString()}`, 'Spend']} labelStyle={{ fontSize: 11 }} />
                    <Bar dataKey="value" fill="#8b5cf6" radius={[3, 3, 0, 0]} name="Subcontract Spend" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
